/**
 * How a job's place and price are written, in one file.
 *
 * Both rules came out of a single published job (`job_b13240e036b8`, Jorge's
 * walkthrough 2026-08-19) that rendered as
 *
 *     📍 ,          $0–$0
 *
 * on the card and again on the detail page. Four call sites drew those two
 * facts, each with its own template, and every one of them assumed the data was
 * there. They live here now so the next surface inherits the rule instead of
 * re-deriving it.
 */

/**
 * "Miami, FL", "Miami", "FL", or null.
 *
 * Null means *render nothing at all* — not an empty string next to a pin icon.
 * A lone pin with a comma after it does not say "location unknown", it says the
 * page is broken.
 */
export function locationText(city, state) {
  const parts = [String(city || "").trim(), String(state || "").trim()]
    .filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

/** Same, from a job document, whose location is nested. */
export function jobLocationText(job) {
  return locationText(job?.location?.city, job?.location?.state);
}

/**
 * "$150–$400", "$150", or the "Flexible" label. Never "$0".
 *
 * Zero is not a budget, it is the absence of one — `JobCreateRequest` defaults
 * both ends to 0 and the conversational draft leaves them null when the client
 * never named a figure. Showing "$0–$0" tells a FixyBee this job pays nothing,
 * which is a different and much worse claim than "the client has not said".
 *
 * `t` is passed rather than imported so this stays usable from the design
 * system, which takes its words from its callers.
 */
export function budgetText(t, min, max) {
  const low = Number(min) || 0;
  const high = Number(max) || 0;
  if (low <= 0 && high <= 0) return t("jobs.budgetFlexible");
  if (low > 0 && high > 0 && low !== high) return `$${low}–$${high}`;
  return `$${Math.max(low, high)}`;
}
